import * as THREE from 'three';
import { WebGLAssets } from "./webgl-assets"

import { Pane } from 'tweakpane';
import * as TweakpanePluginImage from '@kitschpatrol/tweakpane-plugin-image';
import { getStringParam } from '@/utils/urlParams';

type LayeredCanvasImage = {
  src: string;
  width: number;
  height: number;
}

type ParallaxCanvasLayerConfig = {
  x: number;
  y: number;
  z: number;
  scaleValue: number;
}

type LayeredCanvasConfig = {
  parallaxStrength: number;
  layers: ParallaxCanvasLayerConfig[];
}

type LayeredCanvasOptions = {
  container: HTMLElement;
  onCanvasReady?: () => void;
  onHideContent: (hide: boolean) => void;
  onHideGradient: (hide: boolean) => void;
  useTool: boolean
  inducteeName: string;
  images: LayeredCanvasImage[];
  config?: string;
  reduceMotion?: boolean;
};

export default class ParallaxCanvas {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private clock: THREE.Clock;
  private images: LayeredCanvasImage[];
  private readonly baseValues = { x: 0.0, y: 0.0, z: 0.0, scaleValue: 0.0018 };
  private controllerValues: (typeof this.baseValues);
  private layerValues: (typeof this.baseValues)[] = [];

  private container: HTMLElement;
  private onCanvasReady?: () => void;
  private width: number;
  private height: number;
  private cursor: any = {};
  private previousTime: number;
  private parallaxIntensity: number = 0.4;
  private hideHeroContent: boolean = false;
  private hideGradient: boolean = false;
  private invertHorizontal: boolean = false;
  private invertVertical: boolean = false;


  constructor(options: LayeredCanvasOptions) {
    this.container = options.container;
    this.onCanvasReady = options.onCanvasReady;

    if (!options.images || options.images.length === 0) {
      console.warn('No images provided for ParallaxCanvas');
      return;
    }

    this.images = options.images;
    if (options.config) {
      const config: LayeredCanvasConfig = JSON.parse(options.config);
      this.layerValues = config.layers;
      this.parallaxIntensity = config.parallaxStrength;
      if (this.layerValues.length !== this.images.length) {
        const layersToAdd = this.images.length - this.layerValues.length;
        for (let i = 0; i < layersToAdd; i++) {
          this.layerValues.push({ ...this.baseValues });
        }
      }
    } else {
      this.images.forEach(() => {
        this.layerValues.push({ ...this.baseValues });
      });
    }

    this.width = this.container.clientWidth;
    this.height = this.container.clientHeight;

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(45, this.width / this.height, 1, 1000);
    this.camera.position.set(0, 0, 5);
    this.camera.lookAt(new THREE.Vector3(0, 0, 0));

    /////////////////////////////////////////////////////////////
    // Setup: Tweakpane (tool-related)
    // 
    /////////////////////////////////////////////////////////////

    if (options.useTool) {

      this.controllerValues = { ...this.layerValues[0] };

      let currentLayer: number = 0;
      let div = document.createElement("div");
      div.style.background = "grey";
      div.style.color = "white";
      div.style.zIndex = '1000';
      div.style.position = "absolute";
      div.style.left = "10px";
      div.style.top = "80px";
      div.style.padding = "8px";

      let getHeader: any = document.getElementsByTagName("header");
      getHeader[currentLayer].appendChild(div);

      const pane: any = new Pane({
        title: 'Scene Parameters',
        expanded: true,
        container: div
      });
      pane.registerPlugin(TweakpanePluginImage);


      // --------------------------------------------------------------------------------- //
      // -- Add hide controls ----------------------------------------------------- //
      // --------------------------------------------------------------------------------- //  

      pane.addBinding(this, "hideHeroContent", { label: 'Hide Hero Content', view: 'checkbox' }).on('change', (ev: any) => {
        this.hideHeroContent = ev.value;
        options.onHideContent(this.hideHeroContent);
      });

      pane.addBinding(this, "hideGradient", { label: 'Hide Gradient', view: 'checkbox' }).on('change', (ev: any) => {
        this.hideGradient = ev.value;
        options.onHideGradient(this.hideGradient);
      });

      pane.addBinding(this, "invertHorizontal", { label: 'Invert Horizontal', view: 'checkbox' }).on('change', (ev: any) => {
        this.invertHorizontal = ev.value;
      });

      pane.addBinding(this, "invertVertical", { label: 'Invert Vertical', view: 'checkbox' }).on('change', (ev: any) => {
        this.invertVertical = ev.value;
      });

      // --------------------------------------------------------------------------------- //
      // -- add image layers ------------------------------------------------------------- //
      // --------------------------------------------------------------------------------- //  

      const layerOptions = this.images.reduce((acc, _, index) => {
        acc['Layer ' + (index + 1)] = index;
        return acc;
      }, {} as Record<string, number>);

      WebGLAssets.layerController.activeImageUrl = this.images[0].src;
      const activeImage = pane.addBinding(WebGLAssets.layerController, 'activeImageUrl', {
        label: 'Layer Preview',
        disabled: true,
        view: 'input-image',
        imageFit: 'contain',
      });

      pane.addBinding(WebGLAssets.layerController, 'activeLayer', { label: 'Active Layer', options: layerOptions }).on('change', (ev: any) => {
        WebGLAssets.layerController.activeLayer = ev.value;
        WebGLAssets.layerController.activeImageUrl = this.images[ev.value]?.src;

        this.controllerValues.x = this.layerValues[ev.value].x;
        this.controllerValues.y = this.layerValues[ev.value].y;
        this.controllerValues.z = this.layerValues[ev.value].z;
        this.controllerValues.scaleValue = this.layerValues[ev.value].scaleValue;

        setTimeout(() => {
          pane.refresh();
          xPosition.refresh();
          yPosition.refresh();
          zPosition.refresh();
          scaleValue.refresh();
          activeImage.refresh();
        }, 150);
      });

      // --------------------------------------------------------------------------------- //
      // -- Edit parallaxStrength ------------------------------------------------------ //
      // --------------------------------------------------------------------------------- //    
      const globalSettingsFolder = pane.addFolder({
        title: 'Global Settings',
        expanded: true,
      });

      const parallaxValue = globalSettingsFolder.addBinding(this, "parallaxIntensity", { label: 'Parallax Strength', step: 0.01, min: 0.00, max: 2.00 });

      parallaxValue.on('change', (ev: any) => {
        this.parallaxIntensity = ev.value;
      });

      const layerSettingsFolder = pane.addFolder({
        title: 'Layer Settings',
        expanded: true,
      });

      // --------------------------------------------------------------------------------- //
      // -- edit layer´s x-position ------------------------------------------------------ //
      // --------------------------------------------------------------------------------- //    

      let xPosition = layerSettingsFolder.addBinding(this.controllerValues, "x", { label: 'xPos', step: 0.01, min: -10.00, max: 10.00 });

      xPosition.on('change', (ev: any) => {
        const mesh: THREE.Mesh = WebGLAssets.parallaxMeshes[WebGLAssets.layerController.activeLayer] as THREE.Mesh;
        if (mesh) {
          mesh.position.x = ev.value;
          mesh.userData.x = mesh.position.x;
        }
        this.layerValues[WebGLAssets.layerController.activeLayer].x = ev.value;
      });

      // --------------------------------------------------------------------------------- //
      // -- edit layer´s y-position ------------------------------------------------------ //
      // --------------------------------------------------------------------------------- //    

      const yPosition = layerSettingsFolder.addBinding(this.controllerValues, "y", { label: 'yPos', step: 0.01, min: -10.00, max: 10.00 });

      yPosition.on('change', (ev: any) => {
        const mesh: THREE.Mesh = WebGLAssets.parallaxMeshes[WebGLAssets.layerController.activeLayer] as THREE.Mesh;
        if (mesh) {
          mesh.position.y = ev.value;
          mesh.userData.y = mesh.position.y;
        }
        this.layerValues[WebGLAssets.layerController.activeLayer].y = ev.value;
      });

      // --------------------------------------------------------------------------------- //
      // -- edit layer´s y-position ------------------------------------------------------ //
      // --------------------------------------------------------------------------------- //    

      const zPosition = layerSettingsFolder.addBinding(this.controllerValues, "z", { label: 'zPos', step: 0.01, min: -10.00, max: 10.00 });

      zPosition.on('change', (ev: any) => {
        const mesh: THREE.Mesh = WebGLAssets.parallaxMeshes[WebGLAssets.layerController.activeLayer] as THREE.Mesh;
        if (mesh) {
          mesh.position.z = ev.value;
          mesh.userData.z = mesh.position.z;
        }
        this.layerValues[WebGLAssets.layerController.activeLayer].z = ev.value;
      });

      // --------------------------------------------------------------------------------- //
      // -- edit layer´s scale-value ----------------------------------------------------- //
      // --------------------------------------------------------------------------------- //    

      const scaleValue = layerSettingsFolder.addBinding(this.controllerValues, "scaleValue", { label: 'scale', step: 0.00001, min: 0.0001, max: 0.005 });

      scaleValue.on('change', (ev: any) => {
        const mesh: THREE.Mesh = WebGLAssets.parallaxMeshes[WebGLAssets.layerController.activeLayer] as THREE.Mesh;
        if (mesh) {
          mesh.scale.x = mesh.userData.width * ev.value;
          mesh.scale.y = mesh.userData.height * ev.value;
          mesh.userData.scaleValue = ev.value;
        }
        this.layerValues[WebGLAssets.layerController.activeLayer].scaleValue = ev.value;
      });


      // --------------------------------------------------------------------------------- //
      // -- add json-export button ------------------------------------------------------- //
      // --------------------------------------------------------------------------------- //  
      
      pane.addBlade({ view: 'separator' });

      const exportBtn = pane.addButton({
        title: 'Export',
        label: 'Export Config',
      });

      exportBtn.on('click', () => {
        const output = { parallaxStrength: this.parallaxIntensity, layers: this.layerValues };
        let generateJson = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(output, null, 2));
        let syntheticBtn = document.createElement("a");
        syntheticBtn.setAttribute("href", generateJson);
        const baseName = options?.inducteeName ? options?.inducteeName?.toLowerCase().replace(/\s+/g, '-') : 'inductee';
        syntheticBtn.setAttribute("download", `${baseName}-config.json`);
        syntheticBtn.click();
      });
    }

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setSize(this.width, this.height);
    this.container.appendChild(this.renderer.domElement);

    this.clock = new THREE.Clock();
    this.previousTime = 0;

    this.cursor = {
      x: 0,
      y: 0,
      lerpX: 0,
      lerpY: 0,
    };

    window.addEventListener('pointermove', event => {
      event.preventDefault();
      this.cursor.x = event.clientX / this.container.clientWidth - 0.5;
      this.cursor.y = event.clientY / this.container.clientHeight - 0.5;
    }, false);

    this.onResize();
    window.addEventListener('resize', this.onResize);
    this.init();
  }

  init = async () => {
    await Promise.all(this.images.map((img, index) => this.loadLevels(img, index)));
    this.onCanvasReady?.();
    this.render();
  }

  private loadLevels = (img: LayeredCanvasImage, index: number): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      await new THREE.TextureLoader().load(img.src, (texture: THREE.Texture) => {
        const geom: THREE.PlaneGeometry = new THREE.PlaneGeometry(1, 1);
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
          
        const material: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, transparent: true });
        const mesh: THREE.Mesh = new THREE.Mesh(geom, material);
        const { width, height } = img;
        this.scene.add(mesh);

        mesh.position.x += this.layerValues[index].x;
        mesh.position.y += this.layerValues[index].y;
        mesh.position.z += this.layerValues[index].z;

        mesh.scale.x = width * this.layerValues[index].scaleValue;
        mesh.scale.y = height * this.layerValues[index].scaleValue;

        mesh.userData.width = width;
        mesh.userData.height = height;

        WebGLAssets.parallaxMeshes[index] = mesh;
      });
      resolve();
    });
  }

  private render = () => {
    const elapsedTime = this.clock.getElapsedTime();
    const deltaTime = elapsedTime - this.previousTime;
    this.previousTime = elapsedTime;

    const parallaxX = (this.cursor.x * this.parallaxIntensity) * (this.invertHorizontal ? -1 : 1);
    const parallaxY = (-this.cursor.y * this.parallaxIntensity) * (this.invertVertical ? -1 : 1);

    this.cursor.lerpX += (parallaxX - this.cursor.lerpX) * 5 * deltaTime;
    this.cursor.lerpY += (parallaxY - this.cursor.lerpY) * 5 * deltaTime;

    this.camera.position.x = this.cursor.lerpX;
    this.camera.position.y = this.cursor.lerpY;
    this.camera.rotation.y = this.cursor.lerpY * 0.2;
    this.camera.updateProjectionMatrix();

    this.renderer.render(this.scene, this.camera);
    requestAnimationFrame(this.render);
  };



  private onResize = () => {
    this.width = this.container.clientWidth;
    this.height = this.container.clientHeight;
    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.width, this.height);
  };

  public dispose = () => {
    window.removeEventListener('resize', this.onResize);
    this.renderer.dispose();
    WebGLAssets.parallaxMeshes.forEach((mesh) => mesh.geometry.dispose());
    WebGLAssets.parallaxMeshes.forEach((mesh) => (mesh.material as THREE.Material).dispose());
    this.container.removeChild(this.renderer.domElement);
  }
}
